import {
  type HTMLAttributes,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
} from "react";

export type GlassLevel = "thin" | "regular" | "thick";

const levelClass = (level: GlassLevel): string =>
  level === "regular" ? "ao-glass" : `ao-glass ao-glass--${level}`;

/** Journey layer, stylised (not a map projection): Yaoundé bottom left, the destinations around
 * the edges so that the arcs stay visible around a centred card (viewBox 1600 × 900). */
const HOME = { x: 300, y: 720 }; // Yaoundé
const DESTINATIONS = [
  { key: "toronto", x: 150, y: 170, cx: 60, cy: 420, duration: 17 },
  { key: "montreal", x: 420, y: 90, cx: 250, cy: 250, duration: 19 },
  { key: "paris", x: 1180, y: 110, cx: 620, cy: 60, duration: 23 },
  { key: "moscow", x: 1450, y: 190, cx: 900, cy: 20, duration: 26 },
  { key: "dubai", x: 1510, y: 470, cx: 1000, cy: 300, duration: 21 },
] as const;
const MERIDIANS = [0, 1, 2, 3, 4, 5];

/**
 * Animated background, placed once behind the whole app: the mesh gradient, and a "journey" layer
 * (a slowly turning globe, flight arcs from Yaoundé and small planes following them).
 * `vivid` for sign-in pages, `calm` behind working screens. Everything is decorative (aria-hidden),
 * drawn with tokens only, and stops with prefers-reduced-motion or on a slow device (ao-no-glass).
 */
export function Backdrop({ intensity = "calm" }: { intensity?: "calm" | "vivid" }) {
  const id = useId().replace(/:/g, "");
  return (
    <div className={`ao-backdrop ao-backdrop--${intensity}`} aria-hidden="true">
      <svg className="ao-journey" viewBox="0 0 1600 900" preserveAspectRatio="xMinYMid slice" focusable="false">
        <g className="ao-journey__globe">
          <circle cx="1330" cy="760" r="300" className="ao-journey__sphere" />
          {MERIDIANS.map((n) => (
            <ellipse
              key={n}
              cx="1330"
              cy="760"
              rx="300"
              ry="300"
              className="ao-journey__meridian"
              style={{ animationDelay: `${-n * 4}s` }}
            />
          ))}
          {[-200, -100, 0, 100, 200].map((dy) => (
            <ellipse
              key={dy}
              cx="1330"
              cy={760 + dy}
              rx={Math.sqrt(300 ** 2 - dy ** 2)}
              ry="18"
              className="ao-journey__parallel"
            />
          ))}
        </g>
        {DESTINATIONS.map((d, index) => {
          const path = `M${HOME.x} ${HOME.y} Q ${d.cx} ${d.cy} ${d.x} ${d.y}`;
          return (
            <g key={d.key}>
              <path id={`${id}-${d.key}`} d={path} className="ao-journey__arc" />
              <circle cx={d.x} cy={d.y} r="5" className="ao-journey__city" style={{ animationDelay: `${index * 0.7}s` }} />
              <g className="ao-journey__plane">
                <path transform="scale(1.5)" d="M10 0 L3 -1.5 L-1 -8 L-3 -8 L-1 -1.5 L-6 -1.5 L-8 -4 L-9.5 -4 L-8.5 0 L-9.5 4 L-8 4 L-6 1.5 L-1 1.5 L-3 8 L-1 8 L3 1.5 Z" />
                <animateMotion dur={`${d.duration}s`} begin={`${-index * 3}s`} repeatCount="indefinite" rotate="auto">
                  <mpath href={`#${id}-${d.key}`} />
                </animateMotion>
              </g>
            </g>
          );
        })}
        <circle cx={HOME.x} cy={HOME.y} r="7" className="ao-journey__home" />
        <circle cx={HOME.x} cy={HOME.y} r="7" className="ao-journey__pulse" />
      </svg>
    </div>
  );
}

export interface GlassCardProps extends HTMLAttributes<HTMLElement> {
  level?: GlassLevel;
  /** Highlight follows the pointer and the card lifts on hover (featured cards only). */
  interactive?: boolean;
  as?: "section" | "article" | "div" | "aside";
}

export function GlassCard({
  level = "regular",
  interactive = false,
  as: Tag = "section",
  className = "",
  onPointerMove,
  children,
  ...rest
}: GlassCardProps) {
  const handleMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (interactive) {
        const rect = event.currentTarget.getBoundingClientRect();
        event.currentTarget.style.setProperty(
          "--ao-mx",
          `${((event.clientX - rect.left) / rect.width) * 100}%`,
        );
        event.currentTarget.style.setProperty(
          "--ao-my",
          `${((event.clientY - rect.top) / rect.height) * 100}%`,
        );
      }
      onPointerMove?.(event);
    },
    [interactive, onPointerMove],
  );
  const classes = [levelClass(level), "ao-card", interactive && "ao-glass--interactive", className]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag className={classes} onPointerMove={handleMove} {...rest}>
      {children}
    </Tag>
  );
}

/** Sticky navigation bar in thin glass. */
export function GlassBar({ className = "", children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <header className={`ao-glass ao-glass--thin ao-bar ${className}`} {...rest}>
      {children}
    </header>
  );
}

export interface GlassSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/** Modal on desktop, bottom sheet on mobile. Escape and the scrim close it; focus is trapped. */
export function GlassSheet({ open, title, onClose, children }: GlassSheetProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      {/* Keyboard users close with Escape (handled above); the scrim is a mouse/touch shortcut. */}
      <div className="ao-sheet__scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="ao-glass ao-glass--thick ao-sheet"
      >
        <h2 id={titleId}>{title}</h2>
        {children}
      </div>
    </>
  );
}
